/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {useEffect, useState, useCallback} from 'react';
import {useGeocoding} from './use-geocoding';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {useLocation} from 'react-router-dom';

const GEO_REDIRECT_STORAGE_KEY = 'geo_redirect_checked';
const GEO_REDIRECT_EXPIRY_DAYS = 7; // Don't check again for 7 days

/**
 * Custom hook for client-side geo-based redirection
 *
 * This hook:
 * - Uses browser's navigator.geolocation to detect user's location
 * - Calls Google Geocoding API to get country from coordinates
 * - Compares detected country with current site's country
 * - Redirects to appropriate site if country doesn't match
 * - Only runs once per session (with localStorage cache)
 * - Gracefully handles errors without breaking the app
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether geo-redirect is enabled (default: true)
 * @param {boolean} options.debug - Enable debug logging (default: false)
 * @returns {Object} Geo-redirect state and status
 */
export function useGeoRedirect(options = {}) {
  const {enabled = true, debug = false} = options;
  const {site} = useMultiSite();
  const {reverseGeocode, loading, error} = useGeocoding();
  const location = useLocation();

  const [status, setStatus] = useState('idle'); // idle, checking, redirect, no-redirect, error
  const [detectedCountry, setDetectedCountry] = useState(null);

  /**
   * Log debug messages
   */
  const log = useCallback((...args) => {
    if (debug) {
      console.log('[GEO-REDIRECT]', ...args);
    }
  }, [debug]);

  /**
   * Get cached geo data from localStorage
   * Returns null if not cached or expired
   *
   * @returns {Object|null} Cached data with country and timestamp, or null
   */
  const getCachedGeoData = useCallback(() => {
    try {
      const stored = localStorage.getItem(GEO_REDIRECT_STORAGE_KEY);

      if (!stored) {
        return null;
      }

      const data = JSON.parse(stored);
      const expiryTime = new Date(data.timestamp);

      expiryTime.setDate(expiryTime.getDate() + GEO_REDIRECT_EXPIRY_DAYS);

      if (new Date() > expiryTime) {
        // Expired, remove from storage
        localStorage.removeItem(GEO_REDIRECT_STORAGE_KEY);

        return null;
      }

      return data;
    } catch (err) {
      log('Error reading cached geo data:', err);

      return null;
    }
  }, [log]);

  /**
   * Mark geo-redirect as checked
   */
  const markAsChecked = useCallback(() => {
    try {
      localStorage.setItem(
        GEO_REDIRECT_STORAGE_KEY,
        JSON.stringify({
          timestamp: new Date().toISOString(),
          country: detectedCountry,
        })
      );
    } catch (err) {
      log('Error setting localStorage:', err);
    }
  }, [detectedCountry, log]);

  /**
   * Detect if the current browser is a search engine bot/crawler
   * Checks User-Agent for known search engine crawlers
   *
   * @returns {boolean} True if user agent matches a search engine bot
   */
  const isSearchEngineBot = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.userAgent) {
      return false;
    }

    const userAgent = navigator.userAgent.toLowerCase();

    // List of known search engine bot patterns (same as server-side)
    const botPatterns = [
      // Google
      'googlebot',
      'google-inspectiontool',
      'storebot-google',

      // Bing
      'bingbot',
      'msnbot',
      'bingpreview',

      // Other major search engines
      'duckduckbot', // DuckDuckGo
      'baiduspider', // Baidu
      'yandexbot', // Yandex
      'slurp', // Yahoo
      'sogou', // Sogou

      // Social media crawlers (for link previews)
      'facebookexternalhit',
      'twitterbot',
      'linkedinbot',
      'whatsapp',
      'telegrambot',

      // SEO tools and monitoring
      'ahrefsbot',
      'semrushbot',
      'dotbot',
      'rogerbot', // Moz
      'screaming frog',

      // Other crawlers
      'applebot',
      'slackbot',
      'discordbot',
      'pinterestbot',
      'redditbot'
    ];

    // Check if user agent matches any bot pattern
    return botPatterns.some((pattern) => userAgent.includes(pattern));
  }, []);

  /**
   * Check if current path should skip geo-redirect
   * Matches the same skip logic as server-side middleware
   */
  const shouldSkipPath = useCallback(() => {
    const pathname = location.pathname;

    // Get authentication callback paths from site preferences (fallback to defaults)
    const resetPasswordCallback = site.preferences?.resetPasswordCallback || '/reset-password-callback';
    const passwordlessLoginCallback = site.preferences?.passwordlessLoginCallback || '/passwordless-login-callback';
    const socialLoginCallback = site.preferences?.socialLoginCallback || '/social-callback';

    // Skip for authentication, static assets, and system routes (from routes/default.js)
    const skipPaths = [
      // Authentication routes
      '/callback',
      resetPasswordCallback,
      passwordlessLoginCallback,
      socialLoginCallback,

      // Static assets
      '/mobify/',
      '/static/',
      '/assets/',
      '/_next/',

      // Static files
      '/favicon.ico',
      '/robots.txt',
      '/sitemap.xml',

      // Service worker
      '/worker.js',

      // Page routes to skip
      '/checkout',
      '/login',
      '/registration',
      '/reset-password',
      '/passwordless-login-landing'
    ];

    // Check if path starts with any skip path
    if (skipPaths.some((prefix) => pathname.startsWith(prefix))) {
      return true;
    }

    // Skip OAuth JWKS endpoint (pattern: /:shortCode/:tenantId/oauth2/jwks)
    if (pathname.match(/^\/[^/]+\/[^/]+\/oauth2\/jwks$/)) {
      return true;
    }

    return false;
  }, [location.pathname, site.preferences]);

  /**
   * Get browser's geolocation coordinates
   */
  const getBrowserLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));

        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          reject(error);
        },
        {
          timeout: 10000, // 10 seconds timeout
          maximumAge: 300000, // Cache for 5 minutes
          enableHighAccuracy: false, // Faster, less battery drain
        }
      );
    });
  }, []);

  /**
   * Extract country code from geocoding result
   */
  const getCountryCode = useCallback((geocodeResults) => {
    // Try to get the short_name (2-letter country code)
    if (!geocodeResults?.results || geocodeResults.results.length === 0) {
      return null;
    }

    const addressComponents = geocodeResults.results[0].address_components;
    const countryComponent = addressComponents.find((comp) => comp.types.includes('country'));

    return countryComponent ? countryComponent.short_name : null;
  }, []);

  /**
   * Get redirect URL for detected country
   */
  const getRedirectUrl = useCallback((countryCode) => {
    if (!countryCode || !site) {
      return null;
    }

    const upperCountryCode = countryCode.toUpperCase();
    const currentSiteCountryCode = site.countryCode?.toUpperCase();

    log('Current site country:', currentSiteCountryCode);
    log('Detected country:', upperCountryCode);

    // If already on correct site, no redirect needed
    if (upperCountryCode === currentSiteCountryCode) {
      log('User is already on correct site');

      return null;
    }

    // Check if country exists in site preferences
    const countries = site.preferences?.countries || {};
    const countryConfig = countries[upperCountryCode];

    if (!countryConfig) {
      log('No site configuration for country:', upperCountryCode);

      return null;
    }

    const redirectUrl = countryConfig.defaultSiteURL;

    log('Redirect URL found:', redirectUrl);

    return redirectUrl;
  }, [site, log]);

  /**
   * Check redirect using a known country code
   * Used for both cached country and newly detected country
   *
   * @param {string} countryCode - ISO 3166-1 alpha-2 country code
   * @returns {boolean} True if redirect was performed
   */
  const checkAndRedirect = useCallback((countryCode) => {
    if (!countryCode) {
      log('No country code provided');
      setStatus('no-redirect');
      markAsChecked();

      return false;
    }

    log('Checking redirect for country:', countryCode);
    setDetectedCountry(countryCode);

    // Check if redirect is needed
    const redirectUrl = getRedirectUrl(countryCode);

    if (!redirectUrl) {
      log('No redirect needed');
      setStatus('no-redirect');
      markAsChecked();

      return false;
    }

    // Perform redirect
    log('Redirecting to:', redirectUrl);
    setStatus('redirect');

    // Mark as checked before redirect
    markAsChecked();

    // Perform redirect
    window.location.href = redirectUrl;

    return true;
  }, [getRedirectUrl, location.pathname, location.search, markAsChecked, log]);

  /**
   * Perform geo-redirect check with browser geolocation and geocoding
   */
  const performGeoRedirect = useCallback(async () => {
    try {
      setStatus('checking');
      log('Starting geo-redirect check with geocoding...');

      // Get browser location
      log('Getting browser location...');
      const coords = await getBrowserLocation();

      log('Browser location:', coords);

      // Reverse geocode to get country
      log('Reverse geocoding...');
      const geocodeResults = await reverseGeocode(coords.latitude, coords.longitude);

      // Extract country code
      const countryCode = getCountryCode(geocodeResults);

      log('Detected country code:', countryCode);

      if (!countryCode) {
        log('Could not determine country from geocoding');
        setStatus('no-redirect');
        markAsChecked();

        return;
      }

      // Check and perform redirect if needed
      checkAndRedirect(countryCode);
    } catch (err) {
      log('Error during geo-redirect:', err);
      setStatus('error');
      // Mark as checked even on error to avoid infinite loops
      markAsChecked();
    }
  }, [
    getBrowserLocation,
    reverseGeocode,
    getCountryCode,
    checkAndRedirect,
    markAsChecked,
    log
  ]);

  /**
   * Initialize geo-redirect check
   */
  useEffect(() => {
    // Skip if disabled
    if (!enabled) {
      log('Geo-redirect disabled');

      return;
    }

    // Skip for search engine bots to allow proper crawling and SEO
    if (isSearchEngineBot()) {
      log('Skipping geo-redirect for search engine bot');
      setStatus('no-redirect');

      return;
    }

    // Skip for certain paths
    if (shouldSkipPath()) {
      log('Skipping geo-redirect for path:', location.pathname);
      setStatus('no-redirect');

      return;
    }

    // Check if we have cached geo data
    const cachedData = getCachedGeoData();

    if (cachedData?.country) {
      log('Using cached country:', cachedData.country);
      log('Cached timestamp:', cachedData.timestamp);

      const cachedCountryCode = cachedData.country.toUpperCase();
      const currentSiteCountryCode = site.countryCode?.toUpperCase();

      log('Cached country code:', cachedCountryCode);
      log('Current site country code:', currentSiteCountryCode);

      // If cached country matches current site, no redirect needed
      if (cachedCountryCode === currentSiteCountryCode) {
        log('Cached country matches current site, no redirect needed');
        setStatus('no-redirect');

        return;
      }

      // Cached country is different, check if redirect is needed
      log('Cached country differs from current site, checking redirect');
      checkAndRedirect(cachedData.country);

      return;
    }

    // No cached data, perform full geo-redirect check with geocoding
    log('No cached data, performing full geo-redirect check');
    performGeoRedirect();
  }, [enabled, isSearchEngineBot, shouldSkipPath, getCachedGeoData, checkAndRedirect, performGeoRedirect, location.pathname, site.countryCode, log]);

  return {
    status,
    loading,
    error,
    detectedCountry,
  };
}
